/**
 * 🚀 SPACE THEME - Design Vaisseau Spatial
 * Style inspiré Star Wars / Cockpit futuriste
 */

// Couleurs du thème spatial
export const spaceColors = {
  // Fond principal - Noir spatial profond
  background: {
    primary: '#0a0a0f',
    secondary: '#12121a',
    tertiary: '#1a1a25',
    card: 'rgba(15, 15, 25, 0.8)',
    glass: 'rgba(20, 20, 35, 0.6)',
  },
  
  // Accent - Bleu holographique
  hologram: {
    primary: '#00d4ff',
    secondary: '#0099cc',
    glow: 'rgba(0, 212, 255, 0.5)',
    soft: 'rgba(0, 212, 255, 0.1)',
  },
  
  // Orange cockpit (alertes, actions)
  cockpit: {
    primary: '#ff6b00',
    secondary: '#ff8533',
    glow: 'rgba(255, 107, 0, 0.5)',
    soft: 'rgba(255, 107, 0, 0.1)',
  },
  
  // Vert validation / succès
  success: {
    primary: '#00ff88',
    glow: 'rgba(0, 255, 136, 0.5)',
  },
  
  // Rouge alerte
  alert: {
    primary: '#ff3366',
    glow: 'rgba(255, 51, 102, 0.5)',
  },
  
  // Violet Heartbeats
  heartbeats: {
    primary: '#a855f7',
    secondary: '#ec4899',
    glow: 'rgba(168, 85, 247, 0.5)',
  },
  
  // Texte
  text: {
    primary: '#ffffff',
    secondary: '#a0a0b0',
    muted: '#606070',
  }
};

// Styles CSS communs
export const spaceStyles = {
  // Carte holographique
  holoCard: {
    background: spaceColors.background.card,
    backdropFilter: 'blur(20px)',
    border: `1px solid ${spaceColors.hologram.soft}`,
    borderRadius: '16px',
    boxShadow: `0 0 30px ${spaceColors.hologram.glow}, inset 0 0 20px rgba(0, 212, 255, 0.05)`,
  },
  
  // Bouton principal
  primaryButton: {
    background: `linear-gradient(135deg, ${spaceColors.hologram.primary} 0%, ${spaceColors.hologram.secondary} 100%)`,
    boxShadow: `0 0 20px ${spaceColors.hologram.glow}`,
    border: 'none',
    color: '#ffffff',
    fontWeight: 'bold',
  },
  
  // Bouton cockpit (action)
  actionButton: {
    background: `linear-gradient(135deg, ${spaceColors.cockpit.primary} 0%, ${spaceColors.cockpit.secondary} 100%)`,
    boxShadow: `0 0 20px ${spaceColors.cockpit.glow}`,
    border: 'none',
    color: '#ffffff',
    fontWeight: 'bold',
  },
  
  // Grille de données
  dataGrid: {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
  },
  
  // Animation pulse holographique
  holoPulse: {
    animation: 'holoPulse 2s ease-in-out infinite',
  },
};

// Classes Tailwind pour le thème
export const spaceClasses = {
  // Fond principal
  mainBg: 'bg-[#0a0a0f] min-h-screen',
  
  // Carte holographique
  holoCard: 'backdrop-blur-xl rounded-2xl border border-cyan-500/20 bg-[#12121a]/80 shadow-[0_0_30px_rgba(0,212,255,0.15)]',
  
  // Titre holographique
  holoTitle: 'text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 font-bold',
  
  // Texte principal
  textPrimary: 'text-white',
  textSecondary: 'text-gray-400',
  textMuted: 'text-gray-600',
  
  // Bouton holographique
  holoButton: 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold shadow-lg shadow-cyan-500/30 transition-all hover:scale-105',
  
  // Bouton cockpit
  cockpitButton: 'bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-400 hover:to-amber-500 text-white font-bold shadow-lg shadow-orange-500/30 transition-all hover:scale-105',
  
  // Badge
  holoBadge: 'px-2 py-1 rounded-full text-xs font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30',
  
  // Input
  holoInput: 'bg-[#1a1a25] border border-cyan-500/30 rounded-lg text-white placeholder-gray-500 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 focus:outline-none',
  
  // Statistique
  statValue: 'text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400',
  statLabel: 'text-sm text-gray-500 uppercase tracking-wider',
};

// Animation keyframes (à ajouter dans le CSS global ou via styled-components)
export const spaceAnimations = `
  @keyframes holoPulse {
    0%, 100% { box-shadow: 0 0 20px rgba(0, 212, 255, 0.3); }
    50% { box-shadow: 0 0 40px rgba(0, 212, 255, 0.6); }
  }
  
  @keyframes scanLine {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100%); }
  }
  
  @keyframes dataStream {
    0% { opacity: 0; transform: translateY(10px); }
    50% { opacity: 1; }
    100% { opacity: 0; transform: translateY(-10px); }
  }
  
  @keyframes cockpitGlow {
    0%, 100% { box-shadow: 0 0 15px rgba(255, 107, 0, 0.3); }
    50% { box-shadow: 0 0 30px rgba(255, 107, 0, 0.6); }
  }
  
  @keyframes starField {
    0% { background-position: 0 0; }
    100% { background-position: 0 1000px; }
  }
`;

export default { spaceColors, spaceStyles, spaceClasses, spaceAnimations };
